// Page bodies (M7-02): the outline of a BlockNote document (06 "Page — BlockNote document with
// outline"), the search snippet (Work OS `src/domain/docs-library.ts#searchDocs`, e0d760c1: the
// text around the first hit, whitespace folded) and the event copy of the text. Bodies are
// converted with the boards module's `markdownToBlocks` / `blocksToText`.

export type OutlineItem = { block_id: string | null; level: number; text: string };

function inlineText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((c) => {
      if (!c || typeof c !== "object") return "";
      const o = c as { text?: unknown; content?: unknown };
      return typeof o.text === "string" ? o.text : inlineText(o.content);
    })
    .join("");
}

/**
 * Every heading of the document in reading order, nested headings included (BlockNote nests
 * children under a block). Levels outside 1–6 are clamped; empty headings are left out.
 */
export function outlineOf(blocks: readonly unknown[]): OutlineItem[] {
  const out: OutlineItem[] = [];
  const walk = (list: readonly unknown[]) => {
    for (const b of list) {
      if (!b || typeof b !== "object") continue;
      const o = b as { id?: unknown; type?: unknown; props?: unknown; content?: unknown };
      if (o.type === "heading") {
        const text = inlineText(o.content).replace(/\s+/g, " ").trim();
        const raw = (o.props as { level?: unknown } | undefined)?.level;
        const level = typeof raw === "number" && Number.isFinite(raw) ? Math.trunc(raw) : 1;
        if (text !== "")
          out.push({
            block_id: typeof o.id === "string" ? o.id : null,
            level: Math.max(1, Math.min(6, level)),
            text,
          });
      }
      const children = (b as { children?: unknown }).children;
      if (Array.isArray(children)) walk(children);
    }
  };
  walk(blocks);
  return out;
}

/** The text around the first case-insensitive hit of `term` (40 before, 60 after), or null. */
export function snippetOf(text: string, term: string): string | null {
  const t = term.trim();
  if (t === "") return null;
  const i = text.toLowerCase().indexOf(t.toLowerCase());
  if (i < 0) return null;
  return text
    .slice(Math.max(0, i - 40), i + t.length + 60)
    .replace(/\s+/g, " ")
    .trim();
}

/** The body text an event carries (03 `page.edited.body_text`: at most 20,000 characters). */
export const EVENT_TEXT_MAX = 20_000;
export function eventText(text: string): string {
  return text.length > EVENT_TEXT_MAX ? text.slice(0, EVENT_TEXT_MAX) : text;
}

/** `ILIKE` pattern of a search term, with `\`, `%` and `_` escaped (Work OS `searchDocs`). */
export function likePattern(term: string): string {
  return `%${term.trim().replace(/[\\%_]/g, (c) => `\\${c}`)}%`;
}
